import { axiosInstance } from './JsdhNotifikaceService';
import { handleRequest } from '../../axios';
import { CHAT_KANAL_JEDNOTKY_URL, CHAT_WS_TICKET_URL, KONVERZACE_ZPRAVY_URL } from '../url';
import type { ZamekStav } from '../generated/models/OsaResponse';

/**
 * HO-147 Task 4: API vrstva webove stranky kanalu jednotky. Mirror BE DTO
 * z `cz.hasicionline.bean.dto.chat` (ChatController/KonverzaceController).
 */

/** Mirror BE `PosledniZpravaDto`. `nahled` je ZKRACENY NA SERVERU (max 80 znaku)
 * nebo "Příloha" u ciste fotky - nezkracovat znovu na klientovi. */
export interface PosledniZprava {
    autor: string;
    nahled: string;
    cas: string;
}

/**
 * Mirror BE `KanalJednotkyResponse`. Vsech sedm poli je soucast kontraktu.
 * `ztlumeno` a `ztlumenoDo` se MUSI cist spolu - trvale ztlumeni ma
 * `ztlumeno=true` a `ztlumenoDo=null`, tedy stejnou hodnotu jako "nikdy
 * neztlumeno" (viz javadoc BE `KanalJednotkyResponse`).
 */
export interface KanalJednotkyResponse {
    konverzaceId: number;
    zamek: ZamekStav;
    ztlumeno: boolean;
    ztlumenoDo: string | null;
    neprectenych: number;
    /** Samostatna hodnota, NENI podmnozina `neprectenych` (jiny dotaz na BE). */
    neprectenychZminek: number;
    posledniZprava: PosledniZprava | null;
}

/** Mirror BE `ZpravaDto` (projekce zpravy z historie/feedu konverzace). */
export interface ZpravaDto {
    id: number;
    autorId: number | null;
    autorJmeno: string;
    text: string | null;
    createdAt: string;
    upravenoAt: string | null;
    revize: number;
    maPrilohu: boolean;
    smazano: boolean;
    zminky: number[];
}

/** Mirror BE `HistorieStranka` - jedna stranka historie pri prvnim nacteni chatu. */
export interface HistorieStranka {
    zpravy: ZpravaDto[];
    nejstarsiId: number | null;
    maDalsi: boolean;
}

export const getKanalJednotky = () =>
    handleRequest<KanalJednotkyResponse>(() =>
        axiosInstance.get(CHAT_KANAL_JEDNOTKY_URL)
    );

/**
 * Prvni nacteni historie - GET bez `odRevize` (viz `KonverzaceController#zpravy`
 * na BE, rezim urcuje pritomnost `odRevize`, ne `predId`).
 */
export const getHistorieZprav = (konverzaceId: number, limit = 50) =>
    handleRequest<HistorieStranka>(() =>
        axiosInstance.get(KONVERZACE_ZPRAVY_URL(konverzaceId), { params: { limit } })
    );

/**
 * Odeslani textove zpravy. MULTIPART dle BE kontraktu (`posliZpravu` na
 * `KonverzaceController`), i kdyz web zatim (Task 4) neposila prilohu -
 * fotky z chatu jsou pozdejsi task.
 */
export const posliZpravu = (konverzaceId: number, text: string, zpravaUuid: string) => {
    const formData = new FormData();
    formData.append('text', text);
    formData.append('zpravaUuid', zpravaUuid);
    return handleRequest<ZpravaDto>(() =>
        axiosInstance.post(KONVERZACE_ZPRAVY_URL(konverzaceId), formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    );
};

/**
 * HO-152: vydani jednorazoveho WS ticketu pro handshake `/ws/chat`.
 *
 * Refresh token jde v tele - server podle nej dohleda relaci a ticket na ni
 * naveze, takze odhlaseni relace zavre i jeji socket. `MODUL_CHAT` tenhle
 * endpoint zamerne NEGATUJE (ticket je transport, ne obsah).
 */
export const vydejWsTicket = (refreshToken: string) =>
    handleRequest<{ ticket: string }>(() =>
        axiosInstance.post(CHAT_WS_TICKET_URL, { refreshToken })
    );
